import { useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Paperclip, FileText, ExternalLink } from "lucide-react";

// --- formatting helpers ---
const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString()}`;

const formatDate = (date) => {
  if (!date) return "—";
  return format(new Date(date), "dd MMM yyyy");
};

const formatDateTime = (date) => {
  if (!date) return "—";
  return format(new Date(date), "dd MMM yyyy, h:mm a");
};

// "June 2026" from a { month, year } pay period (month is 1-12).
const formatPayPeriod = (pp) => {
  if (!pp || pp.month == null || pp.year == null) return "";
  return format(new Date(pp.year, pp.month - 1, 1), "MMMM yyyy");
};

// --- receipt preview helpers ---
const stripQuery = (url) => String(url || "").split("?")[0];
const isHeicReceipt = (mime, url) =>
  mime === "image/heic" ||
  mime === "image/heif" ||
  (!mime && /\.(heic|heif)$/i.test(stripQuery(url)));
const isImageReceipt = (mime, url) =>
  (!!mime && mime.startsWith("image/")) ||
  (!mime && /\.(jpe?g|png|webp|gif)$/i.test(stripQuery(url)));
const isPdfReceipt = (mime, url) =>
  mime === "application/pdf" || (!mime && /\.pdf$/i.test(stripQuery(url)));

const ReceiptPreview = ({ files }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [errored, setErrored] = useState({});

  if (!files || files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] rounded-lg border border-dashed border-gray-200 dark:border-gray-700 text-muted-foreground">
        <Paperclip className="h-8 w-8 mb-2 opacity-50" />
        <p className="text-sm">No receipt attached</p>
      </div>
    );
  }

  const active = files[activeIndex] || files[0];
  const name = active.name || `Receipt ${activeIndex + 1}`;
  const heic = isHeicReceipt(active.mime_type, active.url);
  const isImage = isImageReceipt(active.mime_type, active.url) && !heic;
  const isPdf = isPdfReceipt(active.mime_type, active.url);
  const useFallback = errored[activeIndex] || heic || (!isImage && !isPdf);

  return (
    <div className="space-y-3">
      <div className="relative h-[400px] rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 overflow-hidden flex items-center justify-center">
        {files.length > 1 && (
          <span className="absolute top-2 left-2 z-10 rounded-md bg-white/90 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 px-2 py-0.5 text-xs text-gray-600 dark:text-gray-300 shadow-sm">
            {activeIndex + 1} / {files.length}
          </span>
        )}
        <a
          href={active.url}
          target="_blank"
          rel="noopener noreferrer"
          className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-md bg-white/90 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 px-2 py-1 text-xs text-gray-700 dark:text-gray-200 hover:bg-white dark:hover:bg-gray-800 shadow-sm"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Open
        </a>

        {isImage && !useFallback ? (
          <img
            src={active.url}
            alt={name}
            className="max-h-full max-w-full object-contain"
            onError={() => setErrored((p) => ({ ...p, [activeIndex]: true }))}
          />
        ) : isPdf && !useFallback ? (
          <iframe title={name} src={active.url} className="w-full h-full" />
        ) : (
          <div className="flex flex-col items-center justify-center text-center px-6 text-muted-foreground">
            <FileText className="h-10 w-10 mb-3 opacity-60" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200 break-all">
              {name}
            </p>
            <p className="text-xs mt-1">Preview not available — open to view</p>
          </div>
        )}
      </div>

      {files.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          {files.map((f, i) => {
            const thumbImg =
              isImageReceipt(f.mime_type, f.url) &&
              !isHeicReceipt(f.mime_type, f.url) &&
              !errored[i];
            return (
              <button
                key={i}
                type="button"
                onClick={() => setActiveIndex(i)}
                title={f.name || `Receipt ${i + 1}`}
                className={`h-14 w-14 rounded-md border overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-900 transition-colors ${i === activeIndex
                  ? "border-blue-500 ring-1 ring-blue-500"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                  }`}
              >
                {thumbImg ? (
                  <img
                    src={f.url}
                    alt=""
                    className="h-full w-full object-cover"
                    onError={() => setErrored((p) => ({ ...p, [i]: true }))}
                  />
                ) : (
                  <FileText className="h-5 w-5 text-muted-foreground" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function ReimbursementDetailsDialog({ open, onOpenChange, claim }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {claim?.employeeName
              ? `Reimbursement Details — ${claim.employeeName}`
              : "Reimbursement Details"}
          </DialogTitle>
        </DialogHeader>
        {claim && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left — claim details */}
            <div className="space-y-3 text-sm">
              {[
                ["Type", claim.claim_type],
                ["Claimed Amount", formatCurrency(claim.amount)],
                ["Expense Date", formatDate(claim.expense_date)],
                ["Status", (claim.status || "").toUpperCase()],
                ["Description", claim.description || "—"],
                [
                  "Submitted",
                  formatDateTime(claim.created_at || claim.applied_date),
                ],
                ...(claim.approved_at
                  ? [["Approved", formatDateTime(claim.approved_at)]]
                  : []),
                ...(claim.rejected_at
                  ? [["Rejected", formatDateTime(claim.rejected_at)]]
                  : []),
                ...(claim.cancelled_at
                  ? [["Cancelled", formatDateTime(claim.cancelled_at)]]
                  : []),
                ...(claim.paid_at
                  ? [
                    [
                      "Paid",
                      `${formatDateTime(claim.paid_at)}${claim.paid_manually
                        ? " · paid manually"
                        : claim.pay_period?.month != null
                          ? ` · ${formatPayPeriod(claim.pay_period)} payroll`
                          : ""
                      }`,
                    ],
                  ]
                  : []),
                ...(claim.admin_comment
                  ? [["Admin Comment", claim.admin_comment]]
                  : []),
                ...(claim.rejection_reason
                  ? [["Rejection Reason", claim.rejection_reason]]
                  : []),
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex justify-between gap-4 border-b border-gray-100 dark:border-gray-800 pb-2"
                >
                  <span className="text-muted-foreground">{label}</span>
                  <span className="font-medium text-right capitalize line-clamp-3">
                    {value}
                  </span>
                </div>
              ))}
            </div>

            {/* Right — receipt preview */}
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Receipts
              </p>
              <ReceiptPreview
                key={claim._id}
                files={
                  claim.receipt_files && claim.receipt_files.length > 0
                    ? claim.receipt_files
                    : (claim.receipt_urls || []).map((url) => ({
                      url,
                      name: null,
                      mime_type: null,
                    }))
                }
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
